<script setup>
import { ref } from "vue";
import { AisSortBy } from "vue-instantsearch/vue3/es";

import { ArrowDown, ListSortIcon } from "@/components/shared/icons";
import { useMyStudentsAlgoliaIndex } from "./composables/useMyStudentsAlgoliaIndex";

const { sortItems } = useMyStudentsAlgoliaIndex();

const isExpanded = ref(false);
const isSortByOpen = ref(false);

// Slot helpers operate on the items array <ais-sort-by> hands back so any future
// transformItems() filtering still resolves correctly.
const findCurrent = (items, currentRefinement) => items.find((item) => item.value === currentRefinement) ?? items[0];

const isDescending = (items, currentRefinement) => findCurrent(items, currentRefinement).order === "desc";

const currentLabel = (items, currentRefinement) => findCurrent(items, currentRefinement).label;

// Unique-by-field list for the dropdown — 4 entries derived from the 8-item Cartesian.
const fieldOptions = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    if (seen.has(item.field.indexPart)) return false;
    seen.add(item.field.indexPart);
    return true;
  });
};

const toggleDirection = (items, currentRefinement, refine) => {
  const current = findCurrent(items, currentRefinement);
  const oppositeOrder = current.order === "asc" ? "desc" : "asc";
  const partner = items.find(
    (item) => item.field.indexPart === current.field.indexPart && item.order === oppositeOrder,
  );
  if (partner) refine(partner.value);
};

const pickField = (items, currentRefinement, refine, field) => {
  const current = findCurrent(items, currentRefinement);
  const target = items.find((item) => item.field.indexPart === field.indexPart && item.order === current.order);
  if (target) refine(target.value);
};

const toggleSortByView = () => {
  isSortByOpen.value = !isSortByOpen.value;
};
</script>
<template>
  <ais-sort-by :items="sortItems">
    <template v-slot="{ items, currentRefinement, refine }">
      <div class="flex flex-nowrap justify-between items-center h-[36px] gap-x-[16px]">
        <button
          class="p-py"
          @mouseover="isExpanded = true"
          @mouseleave="isExpanded = false"
          @click="toggleDirection(items, currentRefinement, refine)"
        >
          <ListSortIcon :is-expanded="isExpanded" :is-descending="isDescending(items, currentRefinement)" />
        </button>
        <div
          class="flex justify-between items-center w-[230px] h-[36px] px-[16px] py-[4px] !bg-white border border-gray-475 rounded-full cursor-pointer"
          @click="toggleSortByView"
        >
          <div>
            <span class="font-[montserrat] text-sm text-university-primary">
              <span class="font-bold">Sort by: </span>{{ currentLabel(items, currentRefinement) }}
            </span>
          </div>
          <div>
            <ArrowDown class="text-black" />
          </div>
          <div
            v-if="isSortByOpen"
            class="drop-shadow-lg rounded-lg bg-white absolute overflow-y-auto z-50 w-full pt-6 pl-3"
            @mouseleave="isSortByOpen = false"
          >
            <ul data-testid="sort-items">
              <li
                v-for="option in fieldOptions(items)"
                :key="option.field.indexPart"
                :data-testid="`sort-${option.field.indexPart}`"
                class="p-2 hover:bg-gray-200 cursor-pointer"
                @click="pickField(items, currentRefinement, refine, option.field)"
              >
                {{ option.label }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>
  </ais-sort-by>
</template>
